import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaFilter,
  FaSyncAlt,
  FaFileDownload,
  FaUserTie,
  FaTruck,
  FaIdCard,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./BreakdownTracker1.css";
import "./DriverList.css";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DriverList = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Y");
  const [typeFilter, setTypeFilter] = useState("");
  const gridRef = useRef(null);
  const navigate = useNavigate();

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/drivers-list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const driverData = response.data?.data || response.data || [];
      setDrivers(Array.isArray(driverData) ? driverData : []);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const handleDelete = async (driverId) => {
    if (window.confirm("Are you sure you want to delete this driver?")) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(
          `${process.env.REACT_APP_BASE_API_URL}/drivers/${driverId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchDrivers();
      } catch (error) {
        console.error("Error deleting driver:", error);
        alert("Failed to delete driver record.");
      }
    }
  };

  const filteredDrivers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return drivers.filter((driver) => {
      const matchesStatus = statusFilter
        ? driver.is_active === statusFilter
        : true;
      const matchesType = typeFilter
        ? (driver.is_expat === "Y" ? "Y" : "N") === typeFilter
        : true;

      const matchesSearch =
        !query ||
        String(driver.reporting_driver_code || "").toLowerCase().includes(query) ||
        String(driver.driver_name || "").toLowerCase().includes(query) ||
        String(driver.truck_number || "").toLowerCase().includes(query) ||
        String(driver.remarks || "").toLowerCase().includes(query);

      return matchesStatus && matchesType && matchesSearch;
    });
  }, [drivers, statusFilter, typeFilter, searchQuery]);

  const resetFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setStatusFilter("");
    setTypeFilter("");
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  const handleExport = () => {
    if (filteredDrivers.length === 0) return;
    const exportData = filteredDrivers.map((d) => ({
      "Driver Code": d.reporting_driver_code || "",
      "Driver Name": d.driver_name || "",
      "Truck Number": d.truck_number || "—",
      Status: d.is_active === "Y" ? "ACTIVE" : "INACTIVE",
      "Driver Type": d.is_expat === "Y" ? "EXPAT" : "LOCAL",
      "Inactive Date": formatDate(d.inactive_date),
      Remarks: d.remarks || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Drivers");
    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Drivers_${date}.xlsx`);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Driver Code",
        field: "reporting_driver_code",
        sortable: true,
        filter: true,
        minWidth: 160,
        flex: 1.1,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              color: "#0f172a",
              background: "#f1f5f9",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "12px",
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Driver Name",
        field: "driver_name",
        sortable: true,
        filter: true,
        minWidth: 220,
        flex: 1.5,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              color: "#1e293b",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaUserTie style={{ color: "#2563eb", fontSize: "13px" }} />
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Truck Number",
        field: "truck_number",
        sortable: true,
        filter: true,
        minWidth: 160,
        flex: 1.1,
        cellRenderer: (params) => (
          <span className="driver-plate-badge">
            <FaTruck style={{ color: "#64748b", fontSize: "11px" }} />
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Driver Type",
        field: "is_expat",
        sortable: true,
        filter: true,
        minWidth: 140,
        flex: 0.9,
        cellRenderer: (params) => {
          const isExpat = params.value === "Y";
          return (
            <span
              className={`driver-type-pill ${isExpat ? "expat" : "local"}`}
            >
              {isExpat ? "Expat" : "Local"}
            </span>
          );
        },
      },
      {
        headerName: "Status",
        field: "is_active",
        sortable: true,
        filter: true,
        minWidth: 140,
        flex: 0.9,
        cellRenderer: (params) => {
          const isActive = params.value === "Y";
          return (
            <span
              className={`driver-status-pill ${isActive ? "active" : "inactive"}`}
            >
              <span className="driver-status-dot" />
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        headerName: "Inactive Date",
        field: "inactive_date",
        sortable: true,
        filter: true,
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
        minWidth: 180,
        flex: 1.3,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              color: "#64748b",
              fontStyle: params.value ? "normal" : "italic",
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 130,
        minWidth: 130,
        sortable: false,
        filter: false,
        pinned: "right",
        cellRenderer: (params) => {
          const driver = params.data;
          if (!driver) return null;
          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                height: "100%",
              }}
            >
              <button
                className="driver-grid-btn edit"
                title="Edit Driver"
                onClick={() => navigate(`/edit-driver/${driver.driver_id}`)}
              >
                <FaEdit /> Edit
              </button>
              <button
                className="driver-grid-btn delete"
                title="Delete Driver"
                onClick={() => handleDelete(driver.driver_id)}
              >
                <FaTrash />
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const hasData = filteredDrivers.length > 0;

  return (
    <div className="driver-management-root bt-container">
      {/* Modern Header */}
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            <button
              className="bt-back-btn"
              onClick={() => navigate("/main")}
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>Driver Management</h1>
            </div>
          </div>

          <div className="bt-header-actions">
            <div className="driver-header-filters">
              <div className="bt-filter-group">
                <FaFilter className="bt-filter-icon" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bt-status-filter"
                  title="Filter by status"
                >
                  <option value="Y">Active Only</option>
                  <option value="N">Inactive Only</option>
                  <option value="">All Status</option>
                </select>
              </div>

              <div className="bt-filter-group">
                <FaIdCard className="bt-filter-icon" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="bt-status-filter"
                  title="Filter by driver type"
                >
                  <option value="">All Types</option>
                  <option value="N">Local</option>
                  <option value="Y">Expat</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bt-main-content">
        {/* Action Bar */}
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button
              onClick={() => navigate("/add-driver")}
              className="bt-primary-btn"
            >
              <FaPlus /> Add New Driver
            </button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input
                type="text"
                placeholder="Search drivers, code or truck..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="bt-search-input"
              />
            </div>
            <button
              className="bt-icon-btn"
              onClick={resetFilters}
              title="Reset table filters"
              aria-label="Reset table filters"
              type="button"
            >
              <FaSyncAlt />
            </button>
            <button
              className="bt-export-btn"
              onClick={handleExport}
              disabled={!hasData}
            >
              <FaFileDownload /> Export Excel
            </button>
          </div>
        </div>

        {/* AG Grid Table View matching Violation/Breakdown/User Tracker */}
        <div className="bt-table-section">
          <div className="bt-table-container">
            {loading ? (
              <div className="bt-empty-state">
                <FaSyncAlt className="bt-empty-icon spinning" />
                <h3>Loading drivers...</h3>
              </div>
            ) : hasData ? (
              <div className="ag-theme-alpine bt-ag-grid">
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredDrivers}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={40}
                  headerHeight={40}
                  pagination={true}
                  paginationPageSize={25}
                  animateRows={true}
                />
              </div>
            ) : (
              <div className="bt-empty-state">
                <FaUserTie className="bt-empty-icon" />
                <h3>No driver records found</h3>
                <p>
                  Try adjusting your search or filters, or add a new driver
                  record
                </p>
                <button
                  onClick={() => navigate("/add-driver")}
                  className="bt-primary-btn"
                >
                  <FaPlus /> Add First Driver
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverList;
